// src/sequencer/Sequencer.js
// Canvas-based multi-track piano-roll style timeline.
//
// Layout:
//   - left:  DOM lane-label column with track name + remove (×) button
//   - right: <canvas> timeline (horizontal: time, vertical: per-track lanes,
//            within a lane: pitch from bottom to top)
//
// Interactions (on the canvas):
//   - click: add a note at the (time, pitch) cell
//   - drag a note body: move
//   - shift+drag right edge: resize duration
//   - right-click a note: delete
//   - delete key: remove selected
//
// The sequencer redraws on any change to trackManager.tracks or
// the transport playhead.

import * as Tone from 'tone';
import { INSTRUMENT_DRAG_MIME } from '../ui/InstrumentPicker.js';
import { t, onLocaleChange } from '../i18n.js';
import { recorder } from '../ui/recorder.js';
import { clipLibrary, ClipLibrary } from '../clips/ClipLibrary.js';

/** True if the drag event carries our custom instrument MIME type. */
function hasInstrumentDrag(e) {
  if (!e.dataTransfer) return false;
  return Array.from(e.dataTransfer.types).includes(INSTRUMENT_DRAG_MIME);
}

const LANE_HEIGHT = 140;        // px per track lane (covers ~2 octaves)
const LABEL_WIDTH = 130;        // left-side label column
const PIXELS_PER_SECOND = 80;   // zoom
const MIDI_LOW = 48;            // C3
const MIDI_HIGH = 84;           // C6
const NOTE_HEIGHT = (MIDI_HIGH - MIDI_LOW + 1) > 0
  ? LANE_HEIGHT / (MIDI_HIGH - MIDI_LOW + 1)
  : 14;

export class Sequencer {
  constructor(rootEl, trackManager) {
    this.root = rootEl;
    this.root.classList.add('sequencer-root');
    this.tm = trackManager;

    // Lane label column (DOM) with track name + remove button per lane.
    this.laneLabels = document.createElement('div');
    this.laneLabels.className = 'lane-labels';
    this.root.appendChild(this.laneLabels);

    // Canvas timeline.
    this.canvas = document.createElement('canvas');
    this.canvas.tabIndex = 0;
    this.canvas.className = 'sequencer-canvas';
    this.root.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // Overlay container for clip drag/resize handles (above the canvas).
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.className = 'clip-overlay-container';
    this.root.appendChild(this.overlayContainer);

    this._clipEditListeners = new Set();

    // Clip resize bindings
    this._onClipResizeDown = this._onClipResizeDown.bind(this);
    this._onClipResizeMove = this._onClipResizeMove.bind(this);
    this._onClipResizeUp = this._onClipResizeUp.bind(this);
    this.overlayContainer.addEventListener('pointerdown', (e) => {
      const handle = e.target.closest('.clip-overlay-handle');
      if (!handle) return;
      this._onClipResizeDown(e, handle.dataset.instanceId, handle.dataset.trackId);
    });

    // Clip drag bindings
    this._onClipDragDown = this._onClipDragDown.bind(this);
    this._onClipDragMove = this._onClipDragMove.bind(this);
    this._onClipDragUp = this._onClipDragUp.bind(this);
    this.overlayContainer.addEventListener('pointerdown', (e) => {
      const drag = e.target.closest('.clip-overlay-drag');
      if (drag) {
        this._onClipDragDown(e, drag.dataset.instanceId, drag.dataset.trackId);
      }
    });

    // Double-click clip overlay to open in ClipEditor
    this.overlayContainer.addEventListener('dblclick', (e) => {
      const drag = e.target.closest('.clip-overlay-drag');
      if (!drag) return;
      const track = this.tm.getTrack(drag.dataset.trackId);
      if (!track) return;
      const inst = track.clips.find((c) => c.id === drag.dataset.instanceId);
      if (!inst) return;
      this._emitClipEdit(inst.clipId);
    });

    // Delegation pointerup on the container catches clicks on overlay
    // elements (belt-and-suspenders with the direct handler on each element).
    this.overlayContainer.addEventListener('pointerup', (e) => {
      if (e.target.closest('.clip-overlay-drag') && !this._dragged) {
        const drag = e.target.closest('.clip-overlay-drag');
        if (!drag) return;
        const track = this.tm.getTrack(drag.dataset.trackId);
        if (!track) return;
        const inst = track.clips.find((c) => c.id === drag.dataset.instanceId);
        if (!inst) return;
        this._selectClipFromOverlay(inst.clipId, inst.id, track.id);
      }
      this._dragged = false;
    });

    this.selectedNoteId = null;
    this.selectedTrackId = null; // the track the NoteEditor is showing
    this.selectedClipDefId = null; // clip definition shown in NoteEditor
    this.selectedClipInstId = null; // clip instance on the track
    this._selectionListeners = new Set();
    this._clipSelectionListeners = new Set();
    this._dragged = false;
    this._overlayEls = new Map(); // instanceId -> { drag, resize } DOM elements
    this.dragState = null;
    this.zoom = PIXELS_PER_SECOND;
    this.arrangementLength = 16; // seconds, grows with content
    this._unsub = this.tm.onChange(() => {
// Re-render on locale change to translate tooltips and ruler labels.
    // Re-render on locale change to translate tooltips and ruler labels.
      // Clear the selection if the selected track was removed.
      if (this.selectedTrackId && !this.tm.getTrack(this.selectedTrackId)) {
        this.setSelectedTrack(null);
      }
      this.arrangementLength = Math.max(8, this.tm.getArrangementLength() + 2);
      this.resize();
      this.renderLabels();
      this.draw();
    });
    // Re-render on locale change to translate tooltips and ruler labels.
    onLocaleChange(() => { this.renderLabels(); this.draw(); });

    this._bindEvents();
    this.resize();
    this.renderLabels();
    this._loop = this._loop.bind(this);
    requestAnimationFrame(this._loop);

    // Track play/pause/stop so the auto-scroll only runs while the
    // transport is actively rolling (avoids fighting the user when
    // they manually scroll the canvas during a pause).
    this.isPlaying = false;
    Tone.Transport.on('start', () => { this.isPlaying = true; });
    Tone.Transport.on('pause', () => { this.isPlaying = false; });
    Tone.Transport.on('stop',  () => { this.isPlaying = false; });

    // While record mode is on, give the canvas a red border so the
    // user can see at-a-glance that piano-key presses are being
    // captured as notes.
    const refreshRecordingClass = () => {
      this.canvas.classList.toggle('sequencer-canvas-recording', recorder.recording);
    };
    refreshRecordingClass();
    recorder.onChange(refreshRecordingClass);
  }

  /**
   * Build/update the DOM lane-label column. Each label is a fixed-height
   * div (LANE_HEIGHT) with a small color stripe, the track name, the note
   * count, and a × remove button. Each row is also a drop target: drag
   * an instrument tile from the picker onto a row to reassign the
   * track's instrument.
   */
  renderLabels() {
    this.laneLabels.innerHTML = '';
    // Ruler spacer to align with the canvas's top ruler (height 22).
    const ruler = document.createElement('div');
    ruler.className = 'lane-label-ruler';
    this.laneLabels.appendChild(ruler);

    for (let i = 0; i < this.tm.tracks.length; i++) {
      const track = this.tm.tracks[i];
      const row = document.createElement('div');
      row.className = 'lane-label';
      row.style.setProperty('--label-color', track.color);
      if (!track.isReady) row.classList.add('lane-label-loading');
      if (track.id === this.selectedTrackId) row.classList.add('selected');
      // Keyboard-accessible selection: Tab to a row, Enter (or Space) to
      // select it. The click handler below covers the mouse path.
      row.setAttribute('data-track-id', track.id);
      row.setAttribute('tabindex', '0');
      row.setAttribute('role', 'button');
      row.setAttribute('aria-label', `Select track ${track.name}`);
      row.addEventListener('click', () => this.setSelectedTrack(track.id));
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.setSelectedTrack(track.id);
        }
      });

      const stripe = document.createElement('div');
      stripe.className = 'lane-label-stripe';

      const body = document.createElement('div');
      body.className = 'lane-label-body';
      const name = document.createElement('div');
      name.className = 'lane-label-name';
      name.textContent = track.name;
      const meta = document.createElement('div');
      meta.className = 'lane-label-meta';
      meta.textContent = `${track.notes.length} note${track.notes.length === 1 ? '' : 's'}` + (track.isReady ? '' : ' • loading');
      body.appendChild(name);
      body.appendChild(meta);

      const x = document.createElement('button');
      x.className = 'lane-label-x';
      x.title = t('mixer.removeTitle');
      x.textContent = '×';
      x.addEventListener('click', (e) => {
        e.stopPropagation();
        this.tm.removeTrack(track.id);
      });

      // Drag-and-drop: accept instrument tiles dropped onto this row.
      // We use a custom MIME (set by InstrumentPicker) to filter out
      // unrelated drags — the dragover handler only prevents default
      // for valid drags, which is what tells the browser this is a
      // drop target.
      row.addEventListener('dragover', (e) => {
        if (!hasInstrumentDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        row.classList.add('lane-label-drop-target');
      });
      row.addEventListener('dragleave', (e) => {
        // dragleave fires when entering a child too; check relatedTarget.
        if (e.relatedTarget && row.contains(e.relatedTarget)) return;
        row.classList.remove('lane-label-drop-target');
      });
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        row.classList.remove('lane-label-drop-target');
        const instrumentId = e.dataTransfer.getData(INSTRUMENT_DRAG_MIME);
        if (!instrumentId) return;
        this.tm.setInstrument(track.id, instrumentId);
      });

      row.appendChild(stripe);
      row.appendChild(body);
      row.appendChild(x);
      this.laneLabels.appendChild(row);
    }
  }

  resize() {
    const dpr = window.devicePixelRatio || 1;
    const totalWidth = LABEL_WIDTH + this.arrangementLength * this.zoom;
    const totalHeight = this.tm.tracks.length * LANE_HEIGHT + 30; // +ruler
    this.canvas.style.width = `${totalWidth}px`;
    this.canvas.style.height = `${totalHeight}px`;
    this.canvas.width = totalWidth * dpr;
    this.canvas.height = totalHeight * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.dpr = dpr;
  }

  _loop() {
    this.draw();
    this._scrollToPlayhead();

    // Sync overlay positions for clip drag/resize handles.
    // We update in-place (rather than innerHTML = '' + rebuild) so the DOM
    // elements persist across frames. This prevents click-jank where a
    // pointerup fires on a destroyed element.
    const container = this.overlayContainer;
    const seen = new Set();
    for (const track of this.tm.tracks) {
      const trackIdx = this.tm.tracks.indexOf(track);
      for (const inst of track.clips) {
        const def = clipLibrary.getClip(inst.clipId);
        if (!def) continue;
        const clipDur = ClipLibrary.getDuration(def.notes);
        const x = LABEL_WIDTH + inst.startTime * this.zoom;
        const w = inst.repeatCount * clipDur * this.zoom;
        const y = this._yForTrack ? this._yForTrack(trackIdx) : trackIdx * LANE_HEIGHT + 22;
        seen.add(inst.id);

        let el = this._overlayEls.get(inst.id);
        if (!el) {
          // Create new overlay elements for this clip instance
          const drag = document.createElement('div');
          drag.className = 'clip-overlay-drag';
          drag.dataset.instanceId = inst.id;
          drag.dataset.trackId = track.id;
          container.appendChild(drag);

          const resize = document.createElement('div');
          resize.className = 'clip-overlay-handle';
          resize.dataset.instanceId = inst.id;
          resize.dataset.trackId = track.id;
          container.appendChild(resize);

          // Click directly on the overlay selects the clip. We do this on
          // the element itself (not via delegation) so it works regardless
          // of pointer-events interaction with the parent container.
          drag.addEventListener('pointerup', (ev) => {
            if (this._dragged) return;
            this._selectClipFromOverlay(inst.clipId, inst.id, track.id);
          });
          el = { drag, resize };
          this._overlayEls.set(inst.id, el);
        }

        // Update positions in-place
        el.drag.style.left = `${x}px`;
        el.drag.style.top = `${y}px`;
        el.drag.style.width = `${w}px`;
        el.drag.style.height = `${LANE_HEIGHT}px`;
        el.resize.style.left = `${x + w - 8}px`;
        el.resize.style.top = `${y}px`;
        el.resize.style.width = '8px';
        el.resize.style.height = `${LANE_HEIGHT}px`;
      }
    }
    // Remove overlays for clip instances that no longer exist
    for (const [id, el] of this._overlayEls) {
      if (!seen.has(id)) {
        el.drag.remove();
        el.resize.remove();
        this._overlayEls.delete(id);
      }
    }

    requestAnimationFrame(this._loop);
  }

  /**
   * Auto-scroll the canvas horizontally so the playhead stays in
   * view while the transport is rolling. The Sequencer's parent
   * element (`this.root`, #sequencer) is the scroll container; the
   * lane-label column is sticky-positioned inside it so the labels
   * remain visible while the canvas scrolls.
   */
  _scrollToPlayhead() {
    if (!this.isPlaying) return;
    const playheadX = LABEL_WIDTH + (Tone.Transport.seconds || 0) * this.zoom;
    // Keep the playhead at ~30% of the visible width from the left so
    // the user can see upcoming notes too.
    const target = Math.max(0, playheadX - this.root.clientWidth * 0.3);
    // Only scroll if we're meaningfully off-target, to avoid micro-jitter
    // on every animation frame.
    if (Math.abs(this.root.scrollLeft - target) > 30) {
      this.root.scrollLeft = target;
    }
  }

  draw() {
    const ctx = this.ctx;
    const W = this.canvas.width / this.dpr;
    const H = this.canvas.height / this.dpr;

    ctx.fillStyle = '#1a1a22';
    ctx.fillRect(0, 0, W, H);

    this._drawRuler(ctx, W);
    this._drawLanes(ctx, H);
    this._drawPlayhead(ctx, H);
  }

  _drawRuler(ctx, W) {
    const bpm = Tone.Transport.bpm.value;
    const secondsPerBeat = 60 / bpm;
    const rulerY = 0;
    const rulerH = 22;

    ctx.fillStyle = '#23232e';
    ctx.fillRect(LABEL_WIDTH, rulerY, W - LABEL_WIDTH, rulerH);

    // Bar lines (every 4 beats = 1 bar in 4/4)
    const totalBeats = this.arrangementLength / secondsPerBeat;
    const beatsPerBar = 4;
    const totalBars = Math.ceil(totalBeats / beatsPerBar);
    ctx.fillStyle = '#c0c0c8';
    ctx.font = '11px monospace';
    ctx.textBaseline = 'middle';
    for (let bar = 0; bar <= totalBars; bar++) {
      const x = LABEL_WIDTH + bar * beatsPerBar * secondsPerBeat * this.zoom;
      ctx.strokeStyle = '#3a3a48';
      ctx.beginPath();
      ctx.moveTo(x, rulerY);
      ctx.lineTo(x, this.canvas.height / this.dpr);
      ctx.stroke();
      ctx.fillText(`${bar + 1}`, x + 4, rulerY + rulerH / 2);
    }
    // Beat ticks
    for (let beat = 0; beat <= totalBeats; beat++) {
      const x = LABEL_WIDTH + beat * secondsPerBeat * this.zoom;
      if (beat % beatsPerBar === 0) continue;
      ctx.strokeStyle = '#2a2a36';
      ctx.beginPath();
      ctx.moveTo(x, rulerY + rulerH - 5);
      ctx.lineTo(x, rulerY + rulerH);
      ctx.stroke();
    }
  }

  _drawLanes(ctx, H) {
    // Prune stale "recent" markers (anything older than the 1.5s flash
    // window). The draw loop runs every frame, so this stays cheap.
    const FLASH_MS = 1500;
    const now = performance.now();
    for (const tr of this.tm.tracks) {
      if (!tr._recentNoteIds || tr._recentNoteIds.size === 0) continue;
      for (const [id, ts] of tr._recentNoteIds) {
        if (now - ts > FLASH_MS) tr._recentNoteIds.delete(id);
      }
    }

    for (let i = 0; i < this.tm.tracks.length; i++) {
      const track = this.tm.tracks[i];
      const yTop = 22 + i * LANE_HEIGHT;
      const yBot = yTop + LANE_HEIGHT;

      // Lane background
      ctx.fillStyle = i % 2 === 0 ? '#1f1f29' : '#22222d';
      ctx.fillRect(0, yTop, this.canvas.width / this.dpr, LANE_HEIGHT);

      // Label
      ctx.fillStyle = '#2a2a35';
      ctx.fillRect(0, yTop, LABEL_WIDTH, LANE_HEIGHT);
      ctx.fillStyle = track.color;
      ctx.fillRect(0, yTop, 4, LANE_HEIGHT);
      ctx.fillStyle = '#e0e0e8';
      ctx.font = '12px sans-serif';
      ctx.textBaseline = 'top';
      ctx.fillText(track.name, 12, yTop + 8);
      ctx.fillStyle = '#888';
      ctx.font = '10px monospace';
      ctx.fillText(`${track.notes.length} notes`, 12, yTop + 26);

      // Selection outline: if this track is the NoteEditor's target,
      // draw a 2px accent border around its lane.
      if (track.id === this.selectedTrackId) {
        ctx.strokeStyle = '#ff5252';
        ctx.lineWidth = 2;
        ctx.strokeRect(0.5, yTop + 0.5, this.canvas.width / this.dpr - 1, LANE_HEIGHT - 1);
      }

      // Pitch grid (subtle horizontal lines per semitone — every 12 = octave highlighted)
      for (let p = MIDI_LOW; p <= MIDI_HIGH; p++) {
        const y = yBot - (p - MIDI_LOW) * NOTE_HEIGHT;
        if (p % 12 === 0) {
          ctx.strokeStyle = '#33333f';
          ctx.beginPath();
          ctx.moveTo(LABEL_WIDTH, y);
          ctx.lineTo(this.canvas.width / this.dpr, y);
          ctx.stroke();
        }
      }

      // Draw clip backgrounds
      const clipBgColor = 'rgba(90, 200, 90, 0.12)'; // soft green tint
      ctx.save();
      for (const inst of track.clips) {
        const def = clipLibrary.getClip(inst.clipId);
        if (!def) continue;
        const clipDur = ClipLibrary.getDuration(def.notes);
        const x = LABEL_WIDTH + inst.startTime * this.zoom;
        const w = inst.repeatCount * clipDur * this.zoom;
        const clipY = this._yForTrack(i);
        ctx.fillStyle = clipBgColor;
        ctx.strokeStyle = 'rgba(90, 200, 90, 0.4)';
        ctx.lineWidth = 1;
        // Rounded rect
        const r = 4;
        ctx.beginPath();
        ctx.moveTo(x + r, clipY);
        ctx.lineTo(x + w - r, clipY);
        ctx.quadraticCurveTo(x + w, clipY, x + w, clipY + r);
        ctx.lineTo(x + w, clipY + LANE_HEIGHT - r);
        ctx.quadraticCurveTo(x + w, clipY + LANE_HEIGHT, x + w - r, clipY + LANE_HEIGHT);
        ctx.lineTo(x + r, clipY + LANE_HEIGHT);
        ctx.quadraticCurveTo(x, clipY + LANE_HEIGHT, x, clipY + LANE_HEIGHT - r);
        ctx.lineTo(x, clipY + r);
        ctx.quadraticCurveTo(x, clipY, x + r, clipY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        // Label
        ctx.fillStyle = 'rgba(90, 200, 90, 0.7)';
        ctx.font = '11px monospace';
        ctx.fillText(`${def.name} ×${inst.repeatCount}`, x + 4, clipY + 14);
      }
      ctx.restore();

      // Draw clip notes
      ctx.save();
      for (const inst of track.clips) {
        const def = clipLibrary.getClip(inst.clipId);
        if (!def) continue;
        const clipDur = ClipLibrary.getDuration(def.notes);
        for (let r = 0; r < inst.repeatCount; r++) {
          const clipOffset = inst.startTime + r * clipDur;
          for (const n of def.notes) {
            const pitchIndex = n.pitch - MIDI_LOW;
            if (pitchIndex < 0 || pitchIndex > MIDI_HIGH - MIDI_LOW) continue;
            const x = LABEL_WIDTH + (clipOffset + n.time) * this.zoom;
            const y = yBot - (pitchIndex + 1) * NOTE_HEIGHT;
            const w = Math.max(2, n.duration * this.zoom);
            ctx.fillStyle = track.color;
            ctx.globalAlpha = 0.4 + n.velocity * 0.6;
            ctx.fillRect(x, y, w, NOTE_HEIGHT - 1);
          }
        }
      }
      ctx.restore();

      // Notes
      for (const n of track.notes) {
        const x = LABEL_WIDTH + n.time * this.zoom;
        const w = Math.max(2, n.duration * this.zoom);
        const noteIndex = n.pitch - MIDI_LOW;
        if (noteIndex < 0 || noteIndex > MIDI_HIGH - MIDI_LOW) continue;
        const y = yBot - (noteIndex + 1) * NOTE_HEIGHT;
        const h = NOTE_HEIGHT - 1;
        const isSelected = n.id === this.selectedNoteId;
        // Compute the "recently added" flash alpha (1.0 → 0 over 1.5s).
        const recentAt = track._recentNoteIds && track._recentNoteIds.get(n.id);
        const recentAge = recentAt ? (performance.now() - recentAt) / 1500 : 1;
        const isRecent = recentAge < 1;
        const flash = isRecent ? (1 - recentAge) : 0; // 1 → 0

        // Dim notes that fall within clip ranges
        const isInClip = track.clips.some((inst) => {
          const def = clipLibrary.getClip(inst.clipId);
          if (!def) return false;
          const clipDur = ClipLibrary.getDuration(def.notes);
          const clipEnd = inst.startTime + inst.repeatCount * clipDur;
          return n.time >= inst.startTime && n.time < clipEnd;
        });

        ctx.fillStyle = isSelected ? '#ffffff' : track.color;
        ctx.globalAlpha = 0.4 + n.velocity * 0.6;
        if (isInClip) ctx.globalAlpha *= 0.3;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1.0;
        if (isRecent) {
          // Bright accent border for newly-recorded notes so the user
          // gets instant visual confirmation that the keypress was
          // captured.
          ctx.strokeStyle = `rgba(255, 82, 82, ${flash})`;
          ctx.lineWidth = 2;
          ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
        } else {
          ctx.strokeStyle = isSelected ? '#fff' : 'rgba(0,0,0,0.4)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
        }
      }
    }

    // Empty state hint
    if (this.tm.tracks.length === 0) {
      ctx.fillStyle = '#666';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(t('seq.empty'), this.canvas.width / this.dpr / 2, 200);
      ctx.textAlign = 'start';
    }
  }

  _yForTrack(trackIdx) {
    return trackIdx * LANE_HEIGHT + 22; // 22px for the ruler
  }

  _drawPlayhead(ctx, H) {
    const pos = Tone.Transport.seconds || 0;
    const x = LABEL_WIDTH + pos * this.zoom;
    const isRec = recorder.recording && this.isPlaying;
    // While recording, draw a translucent red glow around the playhead
    // so the user can see at a glance that notes are being captured.
    if (isRec) {
      const grad = ctx.createLinearGradient(x - 12, 0, x + 12, 0);
      grad.addColorStop(0, 'rgba(255, 82, 82, 0)');
      grad.addColorStop(0.5, 'rgba(255, 82, 82, 0.35)');
      grad.addColorStop(1, 'rgba(255, 82, 82, 0)');
      ctx.fillStyle = grad;
      ctx.fillRect(x - 12, 22, 24, H - 22);
    }
    ctx.strokeStyle = '#ff5252';
    ctx.lineWidth = isRec ? 2 : 1.5;
    ctx.beginPath();
    ctx.moveTo(x, 22);
    ctx.lineTo(x, H);
    ctx.stroke();

    // Triangle on the ruler
    ctx.fillStyle = '#ff5252';
    ctx.beginPath();
    ctx.moveTo(x - 5, 22);
    ctx.lineTo(x + 5, 22);
    ctx.lineTo(x, 28);
    ctx.closePath();
    ctx.fill();
  }

  // ---- Clip resize handlers ----

  _onClipResizeDown(e, instanceId, trackId) {
    e.preventDefault();
    this._resizeState = { instanceId, trackId, startX: e.clientX };
    document.addEventListener('pointermove', this._onClipResizeMove);
    document.addEventListener('pointerup', this._onClipResizeUp);
  }

  _onClipResizeMove(e) {
    const state = this._resizeState;
    if (!state) return;
    const track = this.tm.getTrack(state.trackId);
    if (!track) return;
    const inst = track.clips.find((c) => c.id === state.instanceId);
    if (!inst) return;
    const def = clipLibrary.getClip(inst.clipId);
    if (!def) return;
    const clipDur = ClipLibrary.getDuration(def.notes);
    const dx = e.clientX - state.startX;
    const dt = dx / this.zoom;
    const repeats = Math.max(1, Math.round(dt / clipDur) + inst.repeatCount);
    if (repeats !== inst.repeatCount) {
      track.updateClipInstance(inst.id, { repeatCount: repeats });
      state.startX = e.clientX;
    }
  }

  _onClipResizeUp() {
    this._resizeState = null;
    document.removeEventListener('pointermove', this._onClipResizeMove);
    document.removeEventListener('pointerup', this._onClipResizeUp);
  }

  // ---- Clip drag handlers ----

  _onClipDragDown(e, instanceId, trackId) {
    e.preventDefault();
    this._dragged = false;
    this._dragState = { instanceId, trackId, startX: e.clientX, origTime: 0 };
    const track = this.tm.getTrack(trackId);
    if (track) {
      const inst = track.clips.find((c) => c.id === instanceId);
      if (inst) this._dragState.origTime = inst.startTime;
    }
    document.addEventListener('pointermove', this._onClipDragMove);
    document.addEventListener('pointerup', this._onClipDragUp);
  }

  _onClipDragMove(e) {
    const state = this._dragState;
    if (!state) return;
    this._dragged = true;
    const track = this.tm.getTrack(state.trackId);
    if (!track) return;
    const dx = e.clientX - state.startX;
    const dt = dx / this.zoom;
    // Snap to 16th notes
    const bpm = Tone.Transport.bpm.value;
    const sixteenth = 60 / bpm / 4;
    const newTime = Math.max(0, Math.round((state.origTime + dt) / sixteenth) * sixteenth);
    // Only emit when the value actually changes — 15 redundant emits/s on a
    // wiggling mouse trigger full UI redraws for no benefit.
    if (newTime !== track.clips.find((c) => c.id === state.instanceId)?.startTime) {
      track.updateClipInstance(state.instanceId, { startTime: newTime });
    }
  }

  _onClipDragUp() {
    this._dragState = null;
    document.removeEventListener('pointermove', this._onClipDragMove);
    document.removeEventListener('pointerup', this._onClipDragUp);
  }

  /** Select a clip instance from an overlay element. */
  _selectClipFromOverlay(clipDefId, clipInstId, trackId) {
    const track = this.tm.getTrack(trackId);
    if (!track) return;
    const inst = track.clips.find((c) => c.id === clipInstId);
    if (!inst) return;
    this.setSelectedClip(inst.clipId, inst.id);
  }

  // ---- Interactions ----

  _bindEvents() {
    this.canvas.addEventListener('pointerdown', (e) => this._onMouseDown(e));
    window.addEventListener('pointermove', (e) => this._onMouseMove(e));
    window.addEventListener('pointerup', (e) => this._onMouseUp(e));
    this.canvas.addEventListener('contextmenu', (e) => this._onContextMenu(e));
    window.addEventListener('keydown', (e) => this._onKey(e));
    this.canvas.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });

    // Drag-and-drop: accept instrument tiles dropped onto the canvas too
    // (in case the user drops onto the play area rather than the label).
    this.canvas.addEventListener('dragover', (e) => {
      if (!hasInstrumentDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    });
    this.canvas.addEventListener('drop', (e) => {
      if (!hasInstrumentDrag(e)) return;
      e.preventDefault();
      const instrumentId = e.dataTransfer.getData(INSTRUMENT_DRAG_MIME);
      if (!instrumentId) return;
      // Find which lane the drop landed in (y -> lane index).
      const rect = this.canvas.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const laneIndex = Math.floor((y - 22) / LANE_HEIGHT);
      const track = this.tm.tracks[laneIndex];
      if (track) this.tm.setInstrument(track.id, instrumentId);
    });

    // Drag-and-drop: accept clip library entries dropped onto the sequencer
    this.root.addEventListener('dragover', (e) => {
      e.preventDefault();
    });

    this.root.addEventListener('drop', (e) => {
      const clipId = e.dataTransfer.getData('text/clip-id');
      if (!clipId) return;
      e.preventDefault();
      const rect = this.canvas.getBoundingClientRect();
      const scrollLeft = this.root.scrollLeft || 0;
      const scrollTop = this.root.scrollTop || 0;
      const x = e.clientX - rect.left + scrollLeft - LABEL_WIDTH;
      const time = Math.max(0, x / this.zoom);
      const y = e.clientY - rect.top + scrollTop;
      const trackIdx = Math.floor((y - 22) / LANE_HEIGHT);
      if (trackIdx < 0 || trackIdx >= this.tm.tracks.length) return;
      const track = this.tm.tracks[trackIdx];
      track.addClipInstance(clipId, time, 1);
    });
  }

  _localCoords(e) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }

  _hitTest(x, y) {
    if (x < LABEL_WIDTH || y < 22) return null;
    const laneIndex = Math.floor((y - 22) / LANE_HEIGHT);
    if (laneIndex < 0 || laneIndex >= this.tm.tracks.length) return null;
    const track = this.tm.tracks[laneIndex];
    const time = (x - LABEL_WIDTH) / this.zoom;
    // Find note under cursor
    for (const n of track.notes) {
      const x0 = LABEL_WIDTH + n.time * this.zoom;
      const x1 = x0 + Math.max(2, n.duration * this.zoom);
      const noteIndex = n.pitch - MIDI_LOW;
      if (noteIndex < 0 || noteIndex > MIDI_HIGH - MIDI_LOW) continue;
      const yBot = 22 + (laneIndex + 1) * LANE_HEIGHT;
      const y0 = yBot - (noteIndex + 1) * NOTE_HEIGHT;
      const y1 = y0 + NOTE_HEIGHT;
      // Edge handle on the right (last 6 px)
      const onEdge = x >= x1 - 6 && x <= x1;
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) {
        return { track, note: n, onEdge };
      }
    }
    return { track, time };
  }

  _onMouseDown(e) {
    const { x, y } = this._localCoords(e);

    // Seek the playhead by clicking the ruler or an empty area on any track
    // (not on a note). The ruler is only 22px, so allowing empty-track clicks
    // gives the user a much bigger target. Drag to scrub continuously.
    const isRuler = y < 22;
    const clickedNote = this._hitTest(x, y)?.note;
    if (x >= LABEL_WIDTH && (isRuler || !clickedNote)) {
      const seek = (cx) => {
        Tone.Transport.seconds = Math.max(0, (cx - LABEL_WIDTH) / this.zoom);
      };
      seek(x);
      const onMove = (ev) => {
        ev.preventDefault();
        const pos = this._localCoords(ev);
        if (pos.x >= LABEL_WIDTH) seek(pos.x);
      };
      const onUp = () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
      };
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      return;
    }
    if (y < 22 || x < LABEL_WIDTH) return;

    const hit = this._hitTest(x, y);
    if (!hit) return;

    // Setting/clearing the track selection also affects the NoteEditor.
    this.setSelectedTrack(hit.track ? hit.track.id : null);

    if (hit.note) {
      this.selectedNoteId = hit.note.id;
      this.dragState = {
        type: hit.onEdge && e.shiftKey ? 'resize' : 'move',
        track: hit.track,
        note: hit.note,
        startX: x,
        startY: y,
        origTime: hit.note.time,
        origDuration: hit.note.duration,
        origPitch: hit.note.pitch
      };
    } else {
      // Empty area: add a new note
      const time = Math.max(0, hit.time);
      const yBot = 22 + (this.tm.tracks.indexOf(hit.track) + 1) * LANE_HEIGHT;
      const noteIndex = Math.max(0, Math.min(MIDI_HIGH - MIDI_LOW, Math.floor((yBot - y) / NOTE_HEIGHT)));
      const pitch = MIDI_LOW + noteIndex;
      const newNote = hit.track.addNote({
        time,
        duration: 0.5,
        pitch,
        velocity: 0.8
      });
      this.selectedNoteId = newNote.id;
      this.dragState = {
        type: 'create-resize',
        track: hit.track,
        note: newNote,
        startX: x
      };
    }
    this.draw();
  }

  /**
   * Subscribe to track-selection changes. The NoteEditor uses this to
   * re-render when the user clicks a different track lane.
   */
  onSelectionChange(fn) {
    this._selectionListeners.add(fn);
    return () => this._selectionListeners.delete(fn);
  }

  /**
   * Public selection setter. Updates the DOM (lane-label selected class),
   * the canvas (red border around the active lane), and notifies all
   * listeners (NoteEditor, PianoKeyboard) so they re-render.
   */
  setSelectedTrack(trackId) {
    if (this.selectedTrackId === trackId) return;
    this.selectedTrackId = trackId;
    // Update the existing label rows in place so the visual selection
    // state stays in sync without waiting for the next full re-render.
    if (this.laneLabels) {
      for (const row of this.laneLabels.querySelectorAll('.lane-label')) {
        const isSel = row.getAttribute('data-track-id') === trackId;
        row.classList.toggle('selected', isSel);
      }
    }
    this._selectionListeners.forEach((fn) => fn(trackId));
  }

  /**
   * Subscribe to clip-selection changes. The NoteEditor uses this to
   * show clip notes instead of track notes when a clip is selected.
   */
  onClipSelectionChange(fn) {
    this._clipSelectionListeners.add(fn);
    return () => this._clipSelectionListeners.delete(fn);
  }

  /**
   * Select a clip instance (or deselect by passing null). When a clip is
   * selected, the NoteEditor shows the clip's notes instead of the
   * track's notes. Pass both the clip definition ID and the instance ID.
   */
  setSelectedClip(clipDefId, clipInstId) {
    this.selectedClipDefId = clipDefId;
    this.selectedClipInstId = clipInstId;
    this.selectedNoteId = null;
    // Also update the selected track to the track that contains this clip
    // instance, so recording (which reads selectedTrackId) goes to the
    // right track.
    if (clipInstId) {
      for (const t of this.tm.tracks) {
        if (t.clips.some((c) => c.id === clipInstId)) {
          this.setSelectedTrack(t.id);
          break;
        }
      }
    }
    this._clipSelectionListeners.forEach((fn) => fn(clipDefId));
  }

  /**
   * Subscribe to clip-edit requests (e.g. double-click on a clip overlay
   * or "Edit" in the clip context menu). The ClipEditor uses this to open.
   */
  onClipEdit(fn) {
    this._clipEditListeners.add(fn);
    return () => this._clipEditListeners.delete(fn);
  }

  _emitClipEdit(clipId) {
    this._clipEditListeners.forEach((fn) => fn(clipId));
  }

  _onMouseMove(e) {
    if (!this.dragState) return;
    const { x } = this._localCoords(e);
    const dx = x - this.dragState.startX;
    const dt = dx / this.zoom;

    if (this.dragState.type === 'move') {
      const newTime = Math.max(0, this.dragState.origTime + dt);
      this.dragState.track.updateNote(this.dragState.note.id, { time: snap(newTime) });
    } else if (this.dragState.type === 'resize' || this.dragState.type === 'create-resize') {
      const newDur = Math.max(0.05, this.dragState.origDuration + dt);
      this.dragState.track.updateNote(this.dragState.note.id, { duration: snap(newDur) });
    }
  }

  _onMouseUp() {
    this.dragState = null;
  }

  _onContextMenu(e) {
    e.preventDefault();
    const { x, y } = this._localCoords(e);
    const hit = this._hitTest(x, y);
    if (hit && hit.note) {
      hit.track.removeNote(hit.note.id);
    }
  }

  _onKey(e) {
    // Don't intercept keys while the user is typing in a form field.
    // Otherwise pressing Backspace to clear a value in the NoteEditor
    // would also delete the selected canvas note.
    const target = e.target;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }
    // Only the Delete key removes a note — Backspace is intentionally
    // not bound here, since on many setups it has browser-level
    // "navigate back" behavior and we don't want to delete notes
    // accidentally when the user is just exploring keyboard shortcuts.
    if (e.key === 'Delete') {
      if (this.selectedNoteId) {
        for (const tr of this.tm.tracks) {
          tr.removeNote(this.selectedNoteId);
        }
        this.selectedNoteId = null;
      }
    }
  }

  _onWheel(e) {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      this.zoom = Math.max(20, Math.min(400, this.zoom * factor));
      this.resize();
      this.draw();
    }
  }
}

function snap(t) {
  // Snap to 16th notes based on current BPM
  const bpm = Tone.Transport.bpm.value;
  const sixteenth = 60 / bpm / 4;
  return Math.round(t / sixteenth) * sixteenth;
}
